import React from 'react';
import { Tooltip } from 'antd';
import {
    CrownFilled, CheckCircleFilled,
    CheckOutlined, CloseOutlined,
    ReadOutlined, SolutionOutlined,
} from '@ant-design/icons';
import Swal from 'sweetalert2';
import { calcAge, formatHeight } from '../utils/profileHelpers';

/* ── plan badge ───────────────────────────────────────────────── */
const planBadge = (plan) => {
    if (!plan || plan === 'free') return null;
    const isUltimate = plan === 'ultimate';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            background: isUltimate
                ? 'linear-gradient(135deg,#F59E0B,#FCD34D)'
                : 'linear-gradient(135deg,#AD1457,#D81B60)',
            borderRadius: '6px', padding: '3px 8px',
        }}>
            <CrownFilled style={{ color: isUltimate ? '#78350F' : 'white', fontSize: '10px' }} />
            <span style={{ color: isUltimate ? '#78350F' : 'white', fontSize: '0.65rem', fontWeight: 700 }}>
                {plan.toUpperCase()}
            </span>
        </span>
    );
};

/* status pill for accepted / rejected / cancelled tabs */
const statusConfig = {
    '3': { label: 'Accepted',  color: 'white', bg: '#10B981' },
    '4': { label: 'Rejected',  color: 'white', bg: '#EF4444' },
    '5': { label: 'Cancelled', color: 'white', bg: '#64748B' },
};

/* ── component ────────────────────────────────────────────────── */
const RequestCard = ({ profile, onAccept, onReject, onCancel, activeKey }) => {
    const profileData = profile.sender_profile || profile.receiver_profile;

    const imageUrl = profileData.profile_pictures?.[0]?.image_path
        ? `${process.env.REACT_APP_IMAGE_URL}/${profileData.profile_pictures[0].image_path}`
        : (profileData.gender?.toLowerCase() === 'female'
            ? process.env.REACT_APP_FEMALE_FALLBACK_IMAGE
            : process.env.REACT_APP_MALE_FALLBACK_IMAGE);

    const age        = calcAge(profileData.date_of_birth);
    const height     = formatHeight(profileData.physical_lifestyle?.height);
    const caste      = profileData.socio_religious_background?.caste;
    const denom      = profileData.socio_religious_background?.denomination;
    const education  = profileData.education_occupation?.highest_education;
    const occupation = profileData.education_occupation?.occupation;
    const city       = profileData.contact_information?.city;
    const state      = profileData.contact_information?.state;
    const verified   = profileData.verifications?.name_dob_status === 1;
    const location   = [city, state].filter(Boolean).join(', ');

    const handleCardClick = () => window.open(`/profile?id=${profileData.profile_id}`, '_blank');

    const confirm = (title, text, confirmText, onConfirm) => {
        Swal.fire({
            title, text, icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#AD1457',
            cancelButtonColor: '#94A3B8',
            confirmButtonText: confirmText,
        }).then((r) => { if (r.isConfirmed) onConfirm(); });
    };

    const btnBase = {
        flex: 1, border: 'none', borderRadius: '8px',
        padding: '8px 0', fontSize: '0.82rem', fontWeight: 600,
        cursor: 'pointer', display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: '5px',
        fontFamily: 'Calibri, sans-serif', transition: 'opacity 0.2s',
    };

    const ActionBar = () => {
        const hover = { onMouseEnter: e => e.currentTarget.style.opacity = '0.75', onMouseLeave: e => e.currentTarget.style.opacity = '1' };

        if (activeKey === '1') return (
            <button style={{ ...btnBase, background: 'rgba(239,68,68,0.1)', color: '#EF4444' }} {...hover}
                onClick={e => { e.stopPropagation(); confirm('Cancel Request?', 'This will cancel your sent interest.', 'Yes, cancel it', () => onCancel(profile.id)); }}>
                <CloseOutlined style={{ fontSize: '11px' }} /> Cancel Request
            </button>
        );

        if (activeKey === '2') return (
            <>
                <button style={{ ...btnBase, background: 'rgba(16,185,129,0.12)', color: '#10B981' }} {...hover}
                    onClick={e => { e.stopPropagation(); confirm('Accept Request?', 'You will be connected with this profile.', 'Yes, accept', () => onAccept(profile.id)); }}>
                    <CheckOutlined style={{ fontSize: '11px' }} /> Accept
                </button>
                <button style={{ ...btnBase, background: 'rgba(239,68,68,0.1)', color: '#EF4444' }} {...hover}
                    onClick={e => { e.stopPropagation(); confirm('Reject Request?', 'This interest will be rejected.', 'Yes, reject', () => onReject(profile.id)); }}>
                    <CloseOutlined style={{ fontSize: '11px' }} /> Reject
                </button>
            </>
        );

        if (activeKey === '3') return (
            <button style={{ ...btnBase, background: 'rgba(239,68,68,0.1)', color: '#EF4444' }} {...hover}
                onClick={e => { e.stopPropagation(); confirm('Revoke Acceptance?', 'This will reject the accepted interest.', 'Yes, revoke', () => onReject(profile.id)); }}>
                <CloseOutlined style={{ fontSize: '11px' }} /> Revoke
            </button>
        );

        if (activeKey === '4') return (
            <button style={{ ...btnBase, background: 'rgba(16,185,129,0.12)', color: '#10B981' }} {...hover}
                onClick={e => { e.stopPropagation(); confirm('Accept Request?', 'You will be connected with this profile.', 'Yes, accept', () => onAccept(profile.id)); }}>
                <CheckOutlined style={{ fontSize: '11px' }} /> Accept
            </button>
        );

        return null; // activeKey '5' — no action, status badge is enough
    };

    const status = statusConfig[activeKey];

    return (
        <div
            onClick={handleCardClick}
            style={{
                background: 'white', borderRadius: '16px', overflow: 'hidden',
                border: '1px solid rgba(173,20,87,0.08)',
                transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                fontFamily: 'Calibri, sans-serif',
                display: 'flex', flexDirection: 'column', cursor: 'pointer',
            }}
            onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 12px 48px rgba(173,20,87,0.2),0 4px 12px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = 'rgba(173,20,87,0.2)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(173,20,87,0.08)';
            }}
        >
            {/* ── Photo ── */}
            <div style={{ position: 'relative', height: '220px', overflow: 'hidden', flexShrink: 0 }}>
                <img alt="profile" src={imageUrl} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s ease' }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />

                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%', background: 'linear-gradient(to top,rgba(26,26,46,0.78) 0%,transparent 100%)', pointerEvents: 'none' }} />

                {/* Name + profile ID */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ color: 'white', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                            {profileData.first_name} {profileData.last_name}
                        </span>
                        {verified && (
                            <Tooltip title="Verified Profile">
                                <CheckCircleFilled style={{ color: '#34D399', fontSize: '14px' }} />
                            </Tooltip>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                        {age && (
                            <span style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', borderRadius: '6px', padding: '2px 8px', color: 'rgba(255,255,255,0.9)', fontSize: '0.72rem', fontWeight: 500 }}>
                                {age} yrs
                            </span>
                        )}
                        {height && (
                            <span style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', borderRadius: '6px', padding: '2px 8px', color: 'rgba(255,255,255,0.9)', fontSize: '0.72rem', fontWeight: 500 }}>
                                {height}
                            </span>
                        )}
                    </div>
                </div>

                {/* Plan badge — top right */}
                {profileData.subscription_plan && profileData.subscription_plan !== 'free' && (
                    <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                        {planBadge(profileData.subscription_plan)}
                    </div>
                )}

                {/* Status badge — top left */}
                {status && (
                    <div style={{ position: 'absolute', top: '10px', left: '10px', background: status.bg, backdropFilter: 'blur(8px)', borderRadius: '8px', padding: '3px 10px' }}>
                        <span style={{ color: status.color, fontSize: '0.72rem', fontWeight: 700 }}>{status.label}</span>
                    </div>
                )}
            </div>

            {/* ── Body ── */}
            <div style={{ padding: '12px 14px', flex: 1 }}>
                {/* Caste / denomination tags */}
                {(caste || denom) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                        {caste && (
                            <span style={{ background: '#EDE9FE', color: '#AD1457', borderRadius: '6px', padding: '3px 10px', fontSize: '0.73rem', fontWeight: 600 }}>
                                {caste}
                            </span>
                        )}
                        {denom && (
                            <span style={{ background: '#F0FDF4', color: '#059669', borderRadius: '6px', padding: '3px 10px', fontSize: '0.73rem', fontWeight: 600 }}>
                                {denom}
                            </span>
                        )}
                    </div>
                )}

                {/* Education & Occupation */}
                <div style={{ marginBottom: '8px' }}>
                    {education && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <ReadOutlined style={{ color: '#94A3B8', fontSize: '0.82rem' }} />
                            <span style={{ color: '#475569', fontSize: '0.82rem', lineHeight: 1.3 }}>{education}</span>
                        </div>
                    )}
                    {occupation && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <SolutionOutlined style={{ color: '#94A3B8', fontSize: '0.82rem' }} />
                            <span style={{ color: '#475569', fontSize: '0.82rem', lineHeight: 1.3 }}>{occupation}</span>
                        </div>
                    )}
                    {location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#94A3B8', fontSize: '0.82rem' }}>📍</span>
                            <span style={{ color: '#475569', fontSize: '0.82rem' }}>{location}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Action bar ── */}
            <div style={{ padding: '0 12px 12px', display: 'flex', gap: '8px' }} onClick={e => e.stopPropagation()}>
                <ActionBar />
            </div>
        </div>
    );
};

export default RequestCard;
